import { Link } from 'react-router-dom'

export default function Privacy() {
  return (
    <div className="py-16 px-6">
      <div className="max-w-3xl mx-auto">
        <h1 className="text-3xl font-semibold text-text-primary mb-4">
          Privacy Policy
        </h1>
        <p className="text-sm text-text-secondary mb-2">
          Last updated: 2 February 2026
        </p>
        <p className="text-lg text-text-secondary mb-12">
          Most privacy policies are promises. Ours is an architectural decision. 
          We <strong>can't</strong> violate your privacy because we never had access in the first place.
        </p>

        {/* Google User Data Section - Required for API verification */}
        <section className="mb-12" id="google-user-data">
          <h2 className="text-xl font-semibold text-text-primary mb-4">
            Google User Data
          </h2>
          <p className="text-sm text-text-secondary mb-6">
            This section describes how Praxis CRM accesses, uses, stores, and protects data 
            obtained through Google APIs, in compliance with the{' '}
            <a 
              href="https://developers.google.com/terms/api-services-user-data-policy" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-action-primary hover:underline"
            >
              Google API Services User Data Policy
            </a>.
          </p>

          {/* Data Accessed */}
          <div className="card mb-4">
            <h3 className="font-medium text-text-primary mb-3">Data Accessed</h3>
            <p className="text-sm text-text-secondary mb-3">
              Praxis CRM requests access to the following Google user data:
            </p>
            <ul className="space-y-2 text-sm text-text-secondary list-disc list-inside">
              <li>
                <strong>Google Drive (drive.file scope):</strong> Access to files created 
                or opened by the app only. This is limited to a single folder named 
                "PraxisCRM_Data" that stores your CRM data as JSON files.
              </li>
              <li>
                <strong>Gmail (gmail.readonly scope, optional):</strong> Read-only access 
                to view email threads. This is used solely to display email history for 
                your contacts within the app.
              </li>
              <li>
                <strong>Basic profile information:</strong> Your email address, name, and 
                profile picture for display purposes within the app.
              </li>
            </ul>
          </div>

          {/* Data Usage */}
          <div className="card mb-4">
            <h3 className="font-medium text-text-primary mb-3">Data Usage</h3>
            <p className="text-sm text-text-secondary mb-3">
              Your Google user data is used exclusively to:
            </p>
            <ul className="space-y-2 text-sm text-text-secondary list-disc list-inside">
              <li>Store and retrieve your CRM data (contacts, companies, deals, tasks) in your own Google Drive</li>
              <li>Display email correspondence history with your contacts (if Gmail access is granted)</li>
              <li>Show your name and profile picture in the app interface</li>
            </ul>
            <p className="text-sm text-text-secondary mt-3">
              All data processing occurs entirely within your browser. No Google user data 
              is transmitted to or processed by our servers.
            </p>
          </div>

          {/* Data Sharing */}
          <div className="card mb-4">
            <h3 className="font-medium text-text-primary mb-3">Data Sharing</h3>
            <p className="text-sm text-text-secondary">
              <strong>We do not share your Google user data with any third parties.</strong> This 
              is architecturally enforced: since your data never reaches our servers, we have 
              no data to share. Your CRM data remains in your Google Drive, and your email 
              data is fetched directly from Gmail to your browser without passing through any 
              intermediary server.
            </p>
          </div>

          {/* Data Storage & Protection */}
          <div className="card mb-4">
            <h3 className="font-medium text-text-primary mb-3">Data Storage & Protection</h3>
            <ul className="space-y-2 text-sm text-text-secondary list-disc list-inside">
              <li>
                <strong>No server-side storage:</strong> Praxis CRM is a static web application. 
                We do not operate servers that store or process your data.
              </li>
              <li>
                <strong>Browser-only tokens:</strong> Your Google OAuth access tokens are stored 
                only in your browser's memory and are never transmitted to us.
              </li>
              <li>
                <strong>Your Google Drive:</strong> All CRM data is stored as plain JSON files 
                in your own Google Drive, protected by Google's security infrastructure.
              </li>
              <li>
                <strong>Local browser cache:</strong> The app may cache data locally in your 
                browser for performance. This data never leaves your device.
              </li>
            </ul>
          </div>

          {/* Data Retention & Deletion */}
          <div className="card">
            <h3 className="font-medium text-text-primary mb-3">Data Retention & Deletion</h3>
            <p className="text-sm text-text-secondary mb-3">
              Because we never store your data on our servers, there is no data retention 
              on our end. Your data exists solely in:
            </p>
            <ul className="space-y-2 text-sm text-text-secondary list-disc list-inside mb-4">
              <li><strong>Your Google Drive:</strong> Retained until you delete it</li>
              <li><strong>Your browser:</strong> Cleared when you clear browser data or use the Vault's "Wipe Local Data" feature</li>
            </ul>
            <p className="text-sm text-text-secondary mb-3">
              <strong>To delete your data:</strong>
            </p>
            <ol className="space-y-2 text-sm text-text-secondary list-decimal list-inside">
              <li>Delete the "PraxisCRM_Data" folder from your Google Drive</li>
              <li>Use the Vault page in the app to wipe local browser data</li>
              <li>Revoke Praxis CRM's access from your{' '}
                <a 
                  href="https://myaccount.google.com/permissions" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-action-primary hover:underline"
                >
                  Google Account permissions
                </a>
              </li>
            </ol>
            <p className="text-sm text-text-secondary mt-3">
              Since we have no record of your data, there is no need to contact us for deletion 
              requests. You have full, immediate control.
            </p>
          </div>
        </section>

        {/* External services */}
        <section className="mb-12" id="external-services">
          <h2 className="text-xl font-semibold text-text-primary mb-4">
            External Services & API Calls
          </h2>
          <p className="text-sm text-text-secondary mb-6">
            Some features require external API calls. These requests pass through our proxy 
            server (a Vercel Edge Function) to work around browser security restrictions.
          </p>

          <div className="card mb-4">
            <h3 className="font-medium text-text-primary mb-3">Website Fetching</h3>
            <p className="text-sm text-text-secondary mb-3">
              When you research a company, we fetch their website's public HTML through our proxy.
            </p>
            <ul className="space-y-1 text-sm text-text-secondary list-disc list-inside">
              <li>Only fetches publicly available website content</li>
              <li>Content is not logged or stored on our servers</li>
              <li>Used solely to extract information for your research</li>
            </ul>
          </div>

          <div className="card mb-4">
            <h3 className="font-medium text-text-primary mb-3">Companies House (UK)</h3>
            <p className="text-sm text-text-secondary mb-3">
              We query the UK Companies House public API to enrich company data with official registry information.
            </p>
            <ul className="space-y-1 text-sm text-text-secondary list-disc list-inside">
              <li>Queries public company registry data only</li>
              <li>No personal data is sent to Companies House</li>
              <li>Results are stored in your Google Drive, not our servers</li>
            </ul>
          </div>

          <div className="card mb-4">
            <h3 className="font-medium text-text-primary mb-3">AI Providers</h3>
            <p className="text-sm text-text-secondary mb-3">
              If you configure an AI provider, requests are proxied to your chosen service 
              (OpenAI, Anthropic, Gemini, or OpenRouter).
            </p>
            <ul className="space-y-1 text-sm text-text-secondary list-disc list-inside">
              <li><strong>Your API key:</strong> Encrypted in your browser, stored in your Drive. 
                We never see the plaintext key.</li>
              <li><strong>Your costs:</strong> AI usage is billed directly to your account with 
                your chosen provider.</li>
              <li><strong>Your data:</strong> Research prompts and responses pass through our proxy 
                but are not logged or stored.</li>
              <li><strong>Provider policies:</strong> Your use of AI providers is subject to their 
                respective terms and privacy policies.</li>
            </ul>
          </div>

          <div className="card bg-[#F5F5F3]">
            <h3 className="font-medium text-text-primary mb-3">What the proxy sees</h3>
            <p className="text-sm text-text-secondary mb-3">
              To be transparent: requests do pass through our Vercel Edge Function. Here's what 
              that means:
            </p>
            <ul className="space-y-1 text-sm text-text-secondary list-disc list-inside">
              <li>Vercel's standard request logs may briefly contain IP addresses and timestamps</li>
              <li>We do not add any custom logging of request content</li>
              <li>We do not store, analyse, or monetise any data passing through</li>
              <li>The proxy exists solely to work around browser CORS restrictions</li>
            </ul>
          </div>
        </section>

        {/* Zero knowledge */}
        <section className="mb-12">
          <h2 className="text-xl font-semibold text-text-primary mb-4">
            What "zero-knowledge" actually means
          </h2>
          <div className="card">
            <p className="text-sm text-text-secondary mb-4">
              Not "we pinky promise not to look". We structurally cannot look:
            </p>
            <ul className="space-y-3 text-sm text-text-secondary">
              <li className="flex items-start gap-3">
                <span className="bg-[#F5F5F3] rounded px-2 py-1 text-xs font-mono shrink-0">
                  No database
                </span>
                <span>We don't run one. Your data exists only in your Drive. Full stop.</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="bg-[#F5F5F3] rounded px-2 py-1 text-xs font-mono shrink-0">
                  No server
                </span>
                <span>We host static files. Nothing processes your requests. Nothing phones home.</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="bg-[#F5F5F3] rounded px-2 py-1 text-xs font-mono shrink-0">
                  No tokens
                </span>
                <span>Your auth tokens never leave your browser. We never see them.</span>
              </li>
            </ul>
          </div>
        </section>

        {/* What we don't do */}
        <section className="mb-12">
          <h2 className="text-xl font-semibold text-text-primary mb-4">
            The surveillance we don't do
          </h2>
          <div className="grid md:grid-cols-2 gap-4">
            <div className="card">
              <h3 className="font-medium text-text-primary mb-2 flex items-center gap-2">
                <span className="text-error">x</span> Analytics
              </h3>
              <p className="text-sm text-text-secondary">
                No Google Analytics. No PostHog. No Mixpanel. No "anonymous" telemetry. 
                We don't know how you use this app. We don't want to.
              </p>
            </div>

            <div className="card">
              <h3 className="font-medium text-text-primary mb-2 flex items-center gap-2">
                <span className="text-error">x</span> Error tracking
              </h3>
              <p className="text-sm text-text-secondary">
                No Sentry. No crash reports. If something breaks, it stays between you 
                and your browser console.
              </p>
            </div>

            <div className="card">
              <h3 className="font-medium text-text-primary mb-2 flex items-center gap-2">
                <span className="text-error">x</span> User accounts
              </h3>
              <p className="text-sm text-text-secondary">
                No email harvesting. No user database. No password to leak. 
                Google auth is your identity and we don't keep records.
              </p>
            </div>

            <div className="card">
              <h3 className="font-medium text-text-primary mb-2 flex items-center gap-2">
                <span className="text-error">x</span> Data brokering
              </h3>
              <p className="text-sm text-text-secondary">
                We can't sell what we don't have. No third parties. No "partners". 
                No fine print.
              </p>
            </div>
          </div>
        </section>

        {/* Your control */}
        <section className="mb-12">
          <h2 className="text-xl font-semibold text-text-primary mb-4">
            You're in charge. Actually.
          </h2>
          <div className="card">
            <ul className="space-y-4 text-sm text-text-secondary">
              <li>
                <strong className="text-text-primary">Inspect your data:</strong>{' '}
                Open your Drive. Browse PraxisCRM_Data. It's plain JSON. Read it yourself.
              </li>
              <li>
                <strong className="text-text-primary">Export your data:</strong>{' '}
                Download the folder. Right now. No "export request". No 30-day wait.
              </li>
              <li>
                <strong className="text-text-primary">Delete your data:</strong>{' '}
                Delete the folder. Done. We can't stop you because we don't know it happened.
              </li>
              <li>
                <strong className="text-text-primary">Revoke access:</strong>{' '}
                Remove Praxis from your Google Account. We disappear. Your data stays.
              </li>
              <li>
                <strong className="text-text-primary">Nuke local cache:</strong>{' '}
                Use the Vault to wipe any browser-stored data. Gone. No trace.
              </li>
            </ul>
          </div>
        </section>

        {/* Trust model */}
        <section className="mb-12">
          <h2 className="text-xl font-semibold text-text-primary mb-4">
            Who you're trusting (and who you're not)
          </h2>
          <div className="card bg-[#F5F5F3]">
            <p className="text-sm text-text-secondary mb-4">
              When you use Praxis, you're trusting:
            </p>
            <ol className="space-y-2 text-sm text-text-secondary list-decimal list-inside">
              <li><strong>Your browser</strong> to run the code</li>
              <li><strong>Google</strong> to store your files in Drive</li>
              <li><strong>Our host</strong> to serve static files</li>
            </ol>
            <p className="text-sm text-text-secondary mt-4">
              You're not trusting us. We're not in the loop. We're not even in the room. <strong>We never will be.</strong>
            </p>
          </div>
        </section>

        {/* Contact */}
        <section className="mb-12">
          <h2 className="text-xl font-semibold text-text-primary mb-4">
            Contact
          </h2>
          <div className="card">
            <p className="text-sm text-text-secondary">
              If you have questions about this privacy policy or our data practices, 
              please contact us at{' '}
              <a 
                href="mailto:jon@anotherwebispossible.co.uk" 
                className="text-action-primary hover:underline"
              >
                jon@anotherwebispossible.co.uk
              </a>
            </p>
          </div>
        </section>

        {/* CTA */}
        <section className="text-center pt-8 border-t border-border">
          <p className="text-text-secondary mb-4">
            Curious how it works under the hood?{' '}
            <Link to="/how-it-works" className="text-action-primary hover:underline">
              Read the technical breakdown
            </Link>
          </p>
          <Link to="/login" className="btn btn-primary">
            Connect Google Drive
          </Link>
        </section>
      </div>
    </div>
  )
}
